import { AloftDate, AloftPrice, AnalysisPeriod, IsoMonth } from '@aloft/toolkit-shared';
import { Block, Button, Flex, SimpleInput, Switch, Typography, colors } from '@aloft/toolkit-sui';

import { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { DateOfSaleMethodInput } from '../../redux/features/selectors/calculatedMarketAdjustmentSelectors/selectDateOfSaleOrListToSaleAdjustment.ts';
import {
  getCustomMonthlyPriceMethodChartDisplay,
  getCustomMonthlyPriceMethodTableDisplay,
} from '../../redux/features/selectors/calculatedMarketAdjustmentSelectors/selectMarketAdjustmentsDisplay.ts';
import { selectEffectiveDate } from '../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { CommentaryDateOfSaleTable } from '../commentary/components/CommentaryDateOfSaleTable.tsx';
import { ZillowHomeValueIndexTrendsChartUnwrapped } from '../marketConditions/zillow/ZillowHomeValueIndexTrendsChart.tsx';
import { useMergeCustomMonthPriceTrends } from './hooks/useMergeCustomMonthlyPriceTrends.ts';
import { MarketAdjustmentsModalForm } from './types.ts';

type DataView = 'chart' | 'table';

export const CustomMonthlyPriceMethodContent = ({
  input,
}: {
  input: DateOfSaleMethodInput.CustomMonthlyPrice | undefined;
}) => {
  const [view, setView] = useState<DataView>('chart');
  const effectiveDate = useAppSelector(selectEffectiveDate);

  const { mergeTrends } = useMergeCustomMonthPriceTrends();

  const formContext = useFormContext<MarketAdjustmentsModalForm>();

  const analysisPeriod = formContext.watch(
    'marketAdjustments.cachedForms.dateOfSale.methodState.customMonthlyPrice.analysisPeriod',
  );
  const prevAnalysisPeriod = useRef<AnalysisPeriod>(analysisPeriod);

  const trendsAsPercentagesFormField =
    'marketAdjustments.cachedForms.dateOfSale.methodState.customMonthlyPrice.trendsAsPercentages';
  const trendsAsPercentages = formContext.watch(trendsAsPercentagesFormField);

  const fieldArray = useFieldArray<MarketAdjustmentsModalForm>({
    name: 'marketAdjustments.cachedForms.dateOfSale.methodState.customMonthlyPrice.trends',
  });

  const fields = fieldArray.fields as { id: string; month: IsoMonth; value: string }[]; // types aren't working?

  const chartData = getCustomMonthlyPriceMethodChartDisplay(input);
  const tableData = getCustomMonthlyPriceMethodTableDisplay({ input, maxRowSize: 24 });

  useEffect(() => {
    // make new trends array when analysis period changes
    if (prevAnalysisPeriod.current !== analysisPeriod) {
      prevAnalysisPeriod.current = analysisPeriod;

      const currentTrends = formContext.getValues(
        'marketAdjustments.cachedForms.dateOfSale.methodState.customMonthlyPrice.trends',
      );

      const newTrends = mergeTrends({
        analysisPeriod,
        effectiveDate: AloftDate.schema.parse(effectiveDate),
        previousTrends: currentTrends.map(({ month, value }) => ({
          month,
          value: AloftPrice.fromDollarLike(value).toJSON(),
        })),
      });

      fieldArray.remove();
      fieldArray.replace(
        newTrends.map((trend) => ({
          ...trend,
          value: AloftPrice.schema.parse(trend.value).toDollarLike().toString(),
        })),
      );
    }
  });

  return (
    <Block>
      <Flex
        width="100%"
        className="flex"
        justifyContent="space-between"
        alignItems="baseline"
        backgroundColor="surface1"
        zIndex="1"
        marginBottom="16px"
      >
        <Flex>
          <Typography variant="h3">Price per Month</Typography>
        </Flex>
        <Flex gap="16px" alignItems="center" justify="flex-end">
          <Button
            text="Chart"
            onClick={() => setView('chart')}
            variant="inline"
            __dangerDoNotUseSx={{
              color: view === 'chart' ? colors.textPrimary : colors.textTertiary,
            }}
          />
          <Button
            text="Table"
            onClick={() => setView('table')}
            variant="inline"
            __dangerDoNotUseSx={{
              color: view === 'table' ? colors.textPrimary : colors.textTertiary,
            }}
          />
          <Block width="1px" height="21px" backgroundColor="strokeDefault" />
          <Controller<MarketAdjustmentsModalForm>
            name="marketAdjustments.cachedForms.dateOfSale.methodState.customMonthlyPrice.analysisPeriod"
            render={({ field }) => (
              <Button
                text="6 months"
                onClick={() => field.onChange(6)}
                variant="inline"
                __dangerDoNotUseSx={{
                  color: analysisPeriod === 6 ? colors.textPrimary : colors.textTertiary,
                }}
              />
            )}
          />
          <Controller<MarketAdjustmentsModalForm>
            name="marketAdjustments.cachedForms.dateOfSale.methodState.customMonthlyPrice.analysisPeriod"
            render={({ field }) => (
              <Button
                text="12 months"
                onClick={() => field.onChange(12)}
                variant="inline"
                __dangerDoNotUseSx={{
                  color: analysisPeriod === 12 ? colors.textPrimary : colors.textTertiary,
                }}
              />
            )}
          />
          <Controller<MarketAdjustmentsModalForm>
            name="marketAdjustments.cachedForms.dateOfSale.methodState.customMonthlyPrice.analysisPeriod"
            render={({ field }) => (
              <Button
                text="24 months"
                onClick={() => field.onChange(24)}
                variant="inline"
                __dangerDoNotUseSx={{
                  color: analysisPeriod === 24 ? colors.textPrimary : colors.textTertiary,
                }}
              />
            )}
          />
        </Flex>
      </Flex>
      <Block marginBottom="16px">
        <Switch
          id="trends-as-percentages"
          label="Trends as Percentages"
          value={trendsAsPercentages}
          onChange={() => formContext.setValue(trendsAsPercentagesFormField, !trendsAsPercentages)}
        />
      </Block>
      <Block overflowX="auto" marginBottom="16px">
        <Flex flexDirection="row" gap="8px">
          {fields.map((trend) => (
            <Block key={trend.id} flexShrink={0} width="100px">
              {trend.month}
            </Block>
          ))}
        </Flex>
        <Flex flexDirection="row" gap="8px">
          {fields.map((trend, index) => (
            <Block key={trend.id} flexShrink={0} width="100px">
              <Controller
                name={`marketAdjustments.cachedForms.dateOfSale.methodState.customMonthlyPrice.trends.${index}.value`}
                control={formContext.control}
                render={({ field }) => (
                  <SimpleInput
                    type="number"
                    leftAddon={trendsAsPercentages ? undefined : '$'}
                    rightAddon={trendsAsPercentages ? '%' : undefined}
                    {...field}
                  />
                )}
              />
            </Block>
          ))}
        </Flex>
      </Block>
      {view === 'table' && tableData?.[0] && (
        <Block overflowX="auto">
          <CommentaryDateOfSaleTable data={tableData[0]} />
        </Block>
      )}
      {view === 'chart' && chartData && <ZillowHomeValueIndexTrendsChartUnwrapped {...chartData} />}
    </Block>
  );
};
